import { Stack } from '@koob/margaret';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import {
  KoobAlert,
  KoobButton,
  KoobInputSelectField,
  KoobInputTextareaField,
} from '@koob/ui';
import { TextField } from 'components/Fields';
import RefusalReasonEnum from 'constants/enums/RefusalReasonEnum';

const REFUSAL_REASONS = Object.values(RefusalReasonEnum);

const DETAILS_REQUIRED = [
  RefusalReasonEnum.OTHER,
  RefusalReasonEnum.CONTRACTED_RATE_UNAVAILABLE,
];

const HotelKeeperRefuseBookingForm = ({
  onSubmit,
  onClose,
  onAlternativeProposal,
}) => {
  const { t } = useTranslation('hotelKeeper');

  return (
    <Formik
      initialValues={{
        refusalReasonCode: '',
        hotelKeeperMessage: '',
        originalBookingRef: '',
      }}
      validationSchema={Yup.object().shape({
        refusalReasonCode: Yup.string().required(t('errors:required')),
        hotelKeeperMessage: Yup.string().when('refusalReasonCode', {
          is: (code) => DETAILS_REQUIRED.includes(code),
          then: (schema) => schema.trim().required(t('errors:required')),
        }),
        originalBookingRef: Yup.string(),
      })}
      onSubmit={({ originalBookingRef, ...values }) =>
        onSubmit({
          ...values,
          ...(values.refusalReasonCode ===
            RefusalReasonEnum.DUPLICATE_BOOKING &&
            originalBookingRef && { originalBookingRef }),
        })
      }
    >
      {({ values, isSubmitting, isValid, dirty }) => {
        const reason = values.refusalReasonCode;
        const detailsRequired = DETAILS_REQUIRED.includes(reason);

        return (
          <Form>
            <Stack size="full" direction="column" paddingTop={1} gutterSize={2}>
              <KoobInputSelectField
                name="refusalReasonCode"
                label={t('refusalReason')}
                items={REFUSAL_REASONS}
                renderOption={(code) => t(`refusalReasons.${code}`)}
                required
                data-testid="refuse-booking-form-reason"
              />

              {reason === RefusalReasonEnum.CATEGORY_FULLY_BOOKED && (
                <KoobAlert
                  variant="info"
                  icon="fa-info-circle"
                  description={
                    <Stack direction="column" gutterSize={1}>
                      <span>{t('refusalHints.CATEGORY_FULLY_BOOKED')}</span>
                      <KoobButton
                        type="button"
                        variant="secondary"
                        onClick={onAlternativeProposal}
                      >
                        {t('makeAlternativeProposal')}
                      </KoobButton>
                    </Stack>
                  }
                />
              )}

              {reason === RefusalReasonEnum.CONTRACTED_RATE_UNAVAILABLE && (
                <KoobAlert
                  variant="info"
                  icon="fa-info-circle"
                  description={t('refusalHints.CONTRACTED_RATE_UNAVAILABLE')}
                />
              )}

              {reason === RefusalReasonEnum.DUPLICATE_BOOKING && (
                <TextField
                  name="originalBookingRef"
                  label={t('originalBookingRef')}
                />
              )}

              <KoobInputTextareaField
                name="hotelKeeperMessage"
                label={
                  detailsRequired
                    ? t('additionalDetails')
                    : t('additionalDetailsOptional')
                }
                required={detailsRequired}
                data-testid="refuse-booking-form-textarea"
              />

              <Stack size="full" alignX="flex-end" gutterSize={1}>
                <KoobButton type="button" variant="secondary" onClick={onClose}>
                  {t('misc:cancel')}
                </KoobButton>
                <KoobButton
                  variant="danger"
                  loading={isSubmitting}
                  disabled={!dirty || !isValid}
                  type="submit"
                  testId="refuse-booking-form-button"
                >
                  {t('refuseBooking')}
                </KoobButton>
              </Stack>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default HotelKeeperRefuseBookingForm;
